import React from "react";
import { motion } from "framer-motion";
import {
  Radar, Cpu, GitPullRequest, ArrowUpRight, Activity, Clock,
  ShieldCheck, Zap, Terminal, Check, AlertTriangle, Github,
} from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { BRAND_NAMES } from "../data/brandNames";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const signIn = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const signInGithub = () => {
  window.location.href = `${API}/auth/github/login`;
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const MARQUEE = [
  "ERR_REQUIRE_ESM", "peer dependency conflict", "breaking API regression",
  "vite build failed", "TypeError: undefined", "ERESOLVE", "module not found",
  "deprecated export", "tsconfig mismatch", "stack trace", "migration blocker",
];

function Nav() {
  const { user } = useAuth();
  return (
    <nav className="sticky top-0 z-50 bg-background/85 backdrop-blur border-b-2 border-foreground">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Logo />
        <div className="hidden md:flex items-center gap-8 font-mono text-[13px] uppercase tracking-wide">
          <a href="#pillars" className="hover:text-primary transition-colors" data-testid="nav-pillars">Engine</a>
          <a href="#pricing" className="hover:text-primary transition-colors" data-testid="nav-pricing">Pricing</a>
          <a href="/live" className="hover:text-primary transition-colors flex items-center gap-1.5" data-testid="nav-live">
            <span className="w-1.5 h-1.5 bg-primary live-dot" /> Live Wall
          </a>
          <a href="#naming" className="hover:text-primary transition-colors" data-testid="nav-naming">The Name</a>
        </div>
        {user ? (
          <a href="/dashboard" data-testid="nav-open-dashboard" className="btn btn-dark btn-sm">
            Open Console →
          </a>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={signInGithub} data-testid="nav-signin-github-btn" className="btn btn-dark btn-sm">
              <Github size={15} /> <span className="hidden sm:inline">GitHub</span>
            </button>
            <button onClick={signIn} data-testid="nav-signin-btn" className="btn btn-primary btn-sm">
              Sign in
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header className="relative overflow-hidden border-b-2 border-foreground">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative max-w-7xl mx-auto px-5 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div initial="hidden" animate="show" variants={fade} custom={0}
            className="inline-flex items-center gap-2 border-2 border-foreground bg-card px-3 py-1.5 font-mono text-xs uppercase tracking-wide mb-7">
            <span className="w-2 h-2 bg-primary live-dot" /> Developer telemetry · live from GitHub
          </motion.div>
          <motion.h1 initial="hidden" animate="show" variants={fade} custom={1}
            className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-7xl">
            Intercept developer friction.
            <span className="block text-primary">Ship the fix before</span>
            the issue is filed.
          </motion.h1>
          <motion.p initial="hidden" animate="show" variants={fade} custom={2}
            className="mt-7 font-mono text-base text-muted-foreground max-w-xl leading-relaxed">
            Patchwire streams real distress signals out of open-source repositories, reproduces the
            failing path, and routes a targeted fix straight into the developer's workflow — turning
            silent drop-off into resolved adoption.
          </motion.p>
          <motion.div initial="hidden" animate="show" variants={fade} custom={3}
            className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={signIn} data-testid="hero-signin-btn" className="btn btn-primary btn-lg group">
              Continue with Google
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <button onClick={signInGithub} data-testid="hero-signin-github-btn" className="btn btn-dark btn-lg">
              <Github size={18} /> Continue with GitHub
            </button>
            <a href="#pillars" data-testid="hero-learn-btn" className="btn btn-outline btn-lg">
              How the engine works
            </a>
          </motion.div>
          <motion.div initial="hidden" animate="show" variants={fade} custom={4}
            className="mt-8 flex items-center gap-6 font-mono text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> Zero-dependency capture</span>
            <span className="flex items-center gap-1.5"><Zap size={14} /> No marketing fluff</span>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
          className="lg:col-span-5">
          <div className="bg-foreground text-background border-2 border-foreground shadow-brutal">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-background/20 font-mono text-xs">
              <span className="flex items-center gap-2"><span className="w-2 h-2 bg-primary live-dot" /> friction.feed</span>
              <span className="opacity-60">live</span>
            </div>
            <div className="p-4 space-y-3 font-mono text-xs">
              {[
                { c: "ESM / Module Resolution", t: "ERR_REQUIRE_ESM after v5 upgrade", s: "critical", h: "8.5h" },
                { c: "Dependency Mismatch", t: "ERESOLVE unable to resolve peer dep", s: "high", h: "5.2h" },
                { c: "Breaking API Regression", t: "createClient removed in 3.0", s: "high", h: "4.0h" },
              ].map((r, i) => (
                <div key={i} className="border border-background/20 p-3 bg-background/[0.04]">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-accent">{r.c}</span>
                    <span className={`uppercase text-[10px] px-1.5 py-0.5 border ${r.s === "critical" ? "border-primary text-primary" : "border-accent text-accent"}`}>{r.s}</span>
                  </div>
                  <div className="text-background/90">{r.t}</div>
                  <div className="mt-2 flex items-center justify-between text-background/50">
                    <span className="flex items-center gap-1"><Clock size={11} /> {r.h} lost</span>
                    <span className="flex items-center gap-1 text-accent"><GitPullRequest size={11} /> patch queued</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </header>
  );
}

function Marquee() {
  return (
    <div className="bg-secondary text-secondary-foreground border-b-2 border-foreground py-3 overflow-hidden">
      <div className="marquee">
        {[...MARQUEE, ...MARQUEE].map((m, i) => (
          <span key={i} className="font-mono text-sm px-6 whitespace-nowrap flex items-center gap-6">
            <AlertTriangle size={13} className="text-accent" /> {m}
          </span>
        ))}
      </div>
    </div>
  );
}

const PILLARS = [
  {
    icon: Radar, tag: "01 · Inbound Telemetry",
    title: "The Prophecy Engine",
    body: "Zero-dependency, real-time capture of developer distress signals — public repo events, issue logs and package-registry error flags streamed continuously and resolved to the right maintainer.",
    span: "lg:col-span-7",
  },
  {
    icon: Cpu, tag: "02 · Inference",
    title: "Diagnostic Parser",
    body: "Moves past keyword tracking into algorithmic error reproduction. Parses stack traces and env configs, isolates the exact dependency mismatch or ESM conflict, and generates a targeted workaround.",
    span: "lg:col-span-5",
  },
  {
    icon: GitPullRequest, tag: "03 · Zero-Handoff",
    title: "Contextual Routing",
    body: "Delivers the fix exactly where the developer is looking — programmatic pull requests, targeted markdown forks and repo discussions — with a native email fallback for anti-pitch friction reports.",
    span: "lg:col-span-12",
  },
];

function Pillars() {
  return (
    <section id="pillars" className="max-w-7xl mx-auto px-5 py-24">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">// system architecture</div>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl max-w-2xl leading-[0.95]">
            Three pillars between friction and resolution.
          </h2>
        </div>
        <p className="font-mono text-sm text-muted-foreground max-w-xs">
          An automated, in-context diagnostic engine — not a reactive outreach tool.
        </p>
      </div>
      <div className="grid lg:grid-cols-12 gap-5">
        {PILLARS.map((p, i) => (
          <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
            className={`${p.span} bg-card border-2 border-foreground shadow-brutal p-7 hover-brutal`}>
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-foreground text-background flex items-center justify-center">
                <p.icon size={22} />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{p.tag}</span>
            </div>
            <h3 className="font-display font-extrabold tracking-tight text-2xl mt-6">{p.title}</h3>
            <p className="font-mono text-sm text-muted-foreground mt-3 leading-relaxed max-w-2xl">{p.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const KPIS = [
  { icon: Clock, label: "Time-to-Value", value: "minutes", sub: "from signal posted to fix delivered" },
  { icon: Activity, label: "Friction Interception", value: "pre-ticket", sub: "resolved before a manual support ticket" },
  { icon: Terminal, label: "Conversion via Utility", value: "in the wild", sub: "sign-ups driven by patches developers hit organically" },
];

function Kpis() {
  return (
    <section className="border-y-2 border-foreground bg-muted">
      <div className="max-w-7xl mx-auto px-5 py-16 grid md:grid-cols-3 divide-y-2 md:divide-y-0 md:divide-x-2 divide-foreground">
        {KPIS.map((k, i) => (
          <div key={i} className="px-2 md:px-8 py-6 md:py-0">
            <k.icon size={24} className="text-primary mb-4" />
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{k.label}</div>
            <div className="font-display font-black tracking-tighter text-3xl mt-1">{k.value}</div>
            <div className="font-mono text-sm text-muted-foreground mt-2">{k.sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const TIERS = [
  {
    planId: "free", name: "Open Ecosystem", price: "Free", audience: "Independent OSS maintainers",
    features: ["Automated issue-tracker noise reduction", "Triage filtering to protect maintainer sanity", "Track up to 3 public repositories", "Live friction feed"],
    cta: "Start free", highlight: false,
  },
  {
    planId: "solo", name: "Solo Maintainer", price: "$12", per: "/mo", audience: "Solo & indie maintainers",
    features: ["Everything in Open Ecosystem", "Track up to 15 repositories", "Programmatic Pull Request delivery", "Full diagnostic parser + suggested patches", "Email digest of critical signals", "Single maintainer — no seats"],
    cta: "Go Solo", highlight: false,
  },
  {
    planId: "education", name: "Developer Education", price: "$49", per: "/mo", audience: "Creators · bootcamps · cohorts",
    features: ["Real-time community churn prevention", "Detect students hitting setup regressions", "Course-repo error tracking", "Unlimited repositories", "Cohort adoption analytics"],
    cta: "Choose plan", highlight: true,
  },
  {
    planId: null, name: "Enterprise Telemetry", price: "Custom", audience: "B2B DevTools · IaaS",
    features: ["Adoption Risk Dashboard", "Global developer-hours-lost analytics", "Programmatic PR delivery at scale", "Identity resolution graph", "SSO & dedicated routing pipeline"],
    cta: "Talk to us", highlight: false,
  },
];

function Pricing() {
  const { user } = useAuth();

  const startCheckout = async (planId) => {
    try {
      const res = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  const handleCta = (t) => {
    if (t.planId === null) { window.location.href = "mailto:sales@patchwire.pro?subject=Patchwire%20Enterprise%20Telemetry"; return; }
    if (t.planId === "free") { window.location.href = user ? "/dashboard" : ""; if (!user) signIn(); return; }
    if (!user) { signIn(); return; }
    startCheckout(t.planId);
  };

  return (
    <section id="pricing" className="max-w-7xl mx-auto px-5 py-24">
      <div className="text-center mb-14">
        <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">// monetization</div>
        <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Priced by who you protect.</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TIERS.map((t, i) => (
          <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
            className={`border-2 border-foreground p-6 flex flex-col ${t.highlight ? "bg-foreground text-background shadow-brutal" : "bg-card shadow-brutal-sm"}`}>
            {t.highlight && <div className="font-mono text-xs uppercase tracking-widest text-accent mb-4">★ Most adopted</div>}
            <div className="font-mono text-[11px] uppercase tracking-widest opacity-70">{t.audience}</div>
            <h3 className="font-display font-extrabold tracking-tight text-xl mt-2">{t.name}</h3>
            <div className="mt-3 flex items-end gap-1">
              <span className="font-display font-black tracking-tighter text-4xl">{t.price}</span>
              {t.per && <span className="font-mono text-sm opacity-60 mb-1">{t.per}</span>}
            </div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {t.features.map((f, j) => (
                <li key={j} className="flex items-start gap-2 font-mono text-[13px]">
                  <Check size={15} className={t.highlight ? "text-accent mt-0.5 shrink-0" : "text-primary mt-0.5 shrink-0"} />
                  <span className={t.highlight ? "text-background/90" : "text-foreground"}>{f}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => handleCta(t)} data-testid={`pricing-cta-${i}`}
              className={`btn btn-block mt-6 ${t.highlight ? "btn-primary" : "btn-dark"}`}>
              {t.cta}
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Naming() {
  return (
    <section id="naming" className="border-y-2 border-foreground bg-secondary text-secondary-foreground">
      <div className="max-w-7xl mx-auto px-5 py-24">
        <div className="max-w-2xl mb-12">
          <div className="font-mono text-xs uppercase tracking-widest text-accent mb-3">// the name</div>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
            Why <span className="text-accent">Patchwire</span>.
          </h2>
          <p className="font-mono text-sm text-secondary-foreground/70 mt-5 leading-relaxed">
            This is an automated patch pipeline — fixes delivered straight down the wire — so the name
            should sound like one. Here's the shortlist we generated; Patchwire won.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BRAND_NAMES.map((b, i) => (
            <div key={i} data-testid={`brand-name-${i}`}
              className={`border-2 p-5 ${b.selected ? "bg-accent text-foreground border-foreground shadow-brutal" : "bg-secondary-foreground/[0.05] border-secondary-foreground/25"}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-extrabold tracking-tight text-2xl">{b.name}</span>
                {b.selected && <span className="font-mono text-[10px] uppercase tracking-widest border border-foreground px-1.5 py-0.5">Selected</span>}
              </div>
              <p className={`font-mono text-xs mt-2 ${b.selected ? "text-foreground/80" : "text-secondary-foreground/60"}`}>{b.tagline}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-7xl mx-auto px-5 py-28 text-center">
      <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95] max-w-3xl mx-auto">
        Every unanswered issue is a developer leaving.
      </h2>
      <p className="font-mono text-base text-muted-foreground mt-6 max-w-xl mx-auto">
        Point Patchwire at a repository and watch the friction surface in seconds.
      </p>
      <button onClick={signIn} data-testid="final-signin-btn" className="btn btn-primary btn-lg mt-9">
        Continue with Google <ArrowUpRight size={18} />
      </button>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t-2 border-foreground bg-foreground text-background">
      <div className="max-w-7xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <Logo className="text-background" />
        <p className="font-mono text-xs text-background/60">© 2026 Patchwire · Developer telemetry & automated adoption.</p>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="bg-background">
      <Nav />
      <Hero />
      <Marquee />
      <Pillars />
      <Kpis />
      <Pricing />
      <Naming />
      <FinalCta />
      <Footer />
    </div>
  );
}
